import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import ApiAuth from "../app/api/public/system/ApiAuth";
import StorageLocal from "../inc/StorageLocal";
import { setAuthData } from "../app/reducers/app/actions";

// Контекст авторизации. Пока isAuthReady === false — защищённый UI не рендерим (лоадер).
// Компоненты работают только через useAuth(); токены живут в state.app + StorageLocal.
export const AuthContext = React.createContext(null);

export function AuthProvider({ children }) {
    const dispatch = useDispatch();
    const isAuthenticated = useSelector((state) => state.app.isAuth);
    const accessToken = useSelector((state) => state.app.accessToken);
    const expAccessToken = useSelector((state) => state.app.expAccessToken);
    const permissions = useSelector((state) => state.app.userPermissions);
    const roles = useSelector((state) => state.app.userRoles);
    const user = useSelector((state) => state.app.user);

    const [isAuthReady, setIsAuthReady] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    // Поднимаем сохранённую сессию из StorageLocal; isAuthReady — в любом случае.
    useEffect(() => {
        const saved = StorageLocal.Get(ApiAuth.StorageKey);
        if (saved && saved.accessToken) {
            dispatch(setAuthData({ ...saved, isAuth: true }));
        }
        setIsAuthReady(true);
    }, [dispatch]);

    // Проактивное продление access-токена, ПОКА вкладка открыта — раньше он обновлялся только
    // реактивно, на 401 от какого-нибудь API-вызова (см. ApiModelWithAuth). Если админ просто
    // смотрит трансляцию устройства (её собственные запросы идут мимо ЭТОГО клиента — у
    // control-live.js свой независимый renew через /control/renew-token) и больше ничего не
    // делает, ни один admin-запрос не 401-ится, и accessToken в Redux тихо протухает по TTL
    // (JWT_AT_TTL). При следующей перерисовке iframe'а трансляции (key={deviceId-accessToken}
    // в devices/index.js) в URL уезжает уже мёртвый токен — ControlAuthMiddleware отвечает
    // голым текстом "Unauthorized" вместо страницы, вместо того чтобы просто продлить сессию.
    // Тот же приём, что renewToken()/scheduleTokenRenew в control-live.js, только на уровне SPA:
    // за 5 минут до истечения, не раньше минуты от сейчас; после успешного RefreshToken()
    // expAccessToken в Redux меняется — эффект перезапускается на новый TTL сам, без рекурсии.
    useEffect(() => {
        if (!isAuthenticated || !expAccessToken) return;
        const delay = Math.max(60000, (expAccessToken * 1000 - Date.now()) - 5 * 60 * 1000);
        const timer = setTimeout(() => { ApiAuth.RefreshToken(); }, delay);

        return () => clearTimeout(timer);
    }, [isAuthenticated, expAccessToken]);

    const login = useCallback(async ({ username, password }) => {
        return ApiAuth.Login({ username, password });
    }, []);

    const logout = useCallback(async () => {
        setIsLoggingOut(true);
        try {
            await ApiAuth.Logout();
        } finally {
            setIsLoggingOut(false);
        }
    }, []);

    const refreshToken = useCallback(async () => {
        setIsRefreshing(true);
        try {
            return await ApiAuth.RefreshToken();
        } finally {
            setIsRefreshing(false);
        }
    }, []);

    const value = useMemo(() => ({
        isAuthReady,
        isAuthenticated,
        accessToken,
        permissions,
        roles,
        user,
        isRefreshing,
        isLoggingOut,
        login,
        logout,
        refreshToken,
    }), [
        isAuthReady, isAuthenticated, accessToken, permissions, roles, user,
        isRefreshing, isLoggingOut, login, logout, refreshToken,
    ]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
